// @owners { team: patients-team }
import { Body, Button, Column, H1, Illustration, LgPadding, MdSpacing } from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import React from 'react';
import { getImage } from '~shared/assets/images/getImage';
import { IMG_ALT_TEXTS } from '~shared/constants';
import { DELIVERY_METHOD_COURIER, DELIVERY_METHOD_PICKUP } from '~shared/features/checkout/constants';
import { WHAT_TO_EXPECT } from '~shared/features/shipments/constants';
import { getShipmentUniqueDeliveryMethods } from '~shared/features/shipments/selectors/getShipmentsWithItems';
import { useSelectorShared } from '~shared/store';
import { type Shipment } from '~shared/types';

type Props = {
  shipment: Shipment;
  isSplitOrder: boolean;
};

export const OrderSuccessHeader = ({ shipment, isSplitOrder }: Props) => {
  const { navigate } = useNavigation();
  const deliveryMethods = useSelectorShared((state) =>
    getShipmentUniqueDeliveryMethods(state, {
      id: shipment.id,
    }),
  );
  const isSingleOrderWithUniformDeliveryMethod = !isSplitOrder && deliveryMethods.length === 1;
  const isPickup = isSingleOrderWithUniformDeliveryMethod && deliveryMethods[0] === DELIVERY_METHOD_PICKUP;
  const usePlural = isSplitOrder ? 's' : '';

  return (
    <Column centerHorizontally>
      <Illustration
        source={getImage('bagWithMedications')}
        accessibilityLabel={IMG_ALT_TEXTS.orderBox}
        resizeMode="contain"
        objectFit="contain"
        size="xl"
      />
      <MdSpacing />
      <H1>{`Order${usePlural} scheduled!`}</H1>
      <LgPadding>
        {isSingleOrderWithUniformDeliveryMethod && !isPickup && (
          <Body center>
            {deliveryMethods[0] === DELIVERY_METHOD_COURIER ? WHAT_TO_EXPECT.courier : WHAT_TO_EXPECT.mail}
          </Body>
        )}
      </LgPadding>
      <Button
        type="secondary"
        onPress={() => {
          if (isSplitOrder) {
            navigate('RouteAppTabNavigator', { screen: 'RouteTabOrders' });
          } else {
            navigate('RouteOrderDetails', { shipmentID: shipment.id, state: { origin } });
          }
        }}
      >
        {`View or edit order${usePlural}`}
      </Button>
    </Column>
  );
};
